import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    Loader2,
    CheckCircle,
    XCircle,
    Clock,
    Pause,
    Upload,
    Eye,
    EyeOff,
    Store,
    ZoomIn,
    Check,
    Image as ImageIcon,
    FileText,
    Type,
    X,
    RotateCcw
} from 'lucide-react';
import {
    subscribeToJob,
    cancelAutomationJob,
    formatEstimatedTime,
    calculateRemainingTime,
    AutomationJob
} from '../services/automationService';
import {
    subscribeToJobImages,
    approveImage,
    rejectImage,
    resetImage,
    bulkApproveImages,
    bulkResetImages,
    updateImageSeoMetadata,
    GeneratedImage
} from '../services/generatedImagesService';
import { generateProductImageSeoMetadata } from '../services/seoService';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { Button } from '../components/ui/Button';
import './AutomationDetailPage.css';

export default function AutomationDetailPage() {
    const { jobId } = useParams<{ jobId: string }>();
    const navigate = useNavigate();

    const [job, setJob] = useState<AutomationJob | null>(null);
    const [images, setImages] = useState<GeneratedImage[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({ uploaded: 0, total: 0 });
    const [expandedPrompts, setExpandedPrompts] = useState<Set<string>>(new Set());
    const [seoEdits, setSeoEdits] = useState<Record<string, { filename: string; altText: string }>>({});
    const [zoomedImage, setZoomedImage] = useState<{ url: string; title: string } | null>(null);

    const togglePromptExpand = (imageId: string) => {
        setExpandedPrompts(prev => {
            const newSet = new Set(prev);
            if (newSet.has(imageId)) {
                newSet.delete(imageId);
            } else {
                newSet.add(imageId);
            }
            return newSet;
        });
    };

    useEffect(() => {
        if (!jobId) return;

        const unsubJob = subscribeToJob(jobId, (updatedJob) => {
            setJob(updatedJob);
            setIsLoading(false);
        });

        const unsubImages = subscribeToJobImages(jobId, (updatedImages) => {
            setImages(updatedImages);
        });

        return () => {
            unsubJob();
            unsubImages();
        };
    }, [jobId]);

    // Track upload progress in real-time during upload
    useEffect(() => {
        if (!isUploading || uploadProgress.total === 0) return;

        // Count images that have been uploaded during this upload session
        const uploadedCount = images.filter(img => img.uploadedToShopify === true).length;
        const approvedTotal = images.filter(img => img.status === 'approved' || img.status === 'uploaded').length;

        setUploadProgress(prev => ({
            ...prev,
            uploaded: uploadedCount,
            total: approvedTotal
        }));
    }, [images, isUploading]);

    const handleCancel = async () => {
        if (!jobId || !confirm('Êtes-vous sûr de vouloir annuler cette automatisation ?')) return;
        await cancelAutomationJob(jobId);
    };

    const handleApprove = async (imageId: string) => {
        // Save SEO data before approving
        const seoData = seoEdits[imageId];
        if (seoData) {
            await updateImageSeoMetadata(imageId, {
                seoFilename: seoData.filename,
                altText: seoData.altText,
            });
        }
        await approveImage(imageId);
    };

    const handleReject = async (imageId: string) => {
        await rejectImage(imageId);
    };

    // Initialize SEO values when images load
    useEffect(() => {
        if (images.length === 0) return;

        setSeoEdits(prevEdits => {
            const newEdits = { ...prevEdits };
            let hasChanges = false;

            for (const image of images) {
                if (!newEdits[image.id]) {
                    hasChanges = true;
                    // Calculate SEO values using the shared service
                    const seoMetadata = generateProductImageSeoMetadata(
                        image.productTitle,
                        images.filter(i => i.productId === image.productId && i.createdAt < image.createdAt).length
                    );

                    // Use stored values if they exist, otherwise use calculated ones
                    newEdits[image.id] = {
                        filename: image.seoFilename || seoMetadata.filename.replace('.webp', ''),
                        altText: image.altText || seoMetadata.altText,
                    };
                }
            }

            return hasChanges ? newEdits : prevEdits;
        });
    }, [images]);

    const updateSeoEdit = (imageId: string, field: 'filename' | 'altText', value: string) => {
        setSeoEdits(prev => ({
            ...prev,
            [imageId]: {
                ...prev[imageId],
                [field]: value,
            },
        }));
    };

    const handleBulkApprove = async () => {
        if (selectedImages.size === 0) return;
        await bulkApproveImages(Array.from(selectedImages));
        setSelectedImages(new Set());
    };

    const handleReset = async (imageId: string) => {
        await resetImage(imageId);
    };

    const handleBulkReset = async () => {
        if (selectedImages.size === 0) return;
        await bulkResetImages(Array.from(selectedImages));
        setSelectedImages(new Set());
    };

    const handleClearSelection = () => {
        setSelectedImages(new Set());
    };

    const handleUploadApproved = async () => {
        if (!jobId) return;

        // Set initial progress with total before starting
        const toUpload = images.filter(img => img.status === 'approved' && !img.uploadedToShopify);
        setUploadProgress({ uploaded: 0, total: toUpload.length });
        setIsUploading(true);

        try {
            const uploadApprovedImages = httpsCallable(functions, 'uploadApprovedImages');
            const result = await uploadApprovedImages({ jobId });
            const data = result.data as any;
            setUploadProgress({ uploaded: data.uploaded, total: data.total });
        } catch (error) {
            console.error('Error uploading images:', error);
        } finally {
            setIsUploading(false);
        }
    };

    const toggleImageSelection = (imageId: string) => {
        const newSelected = new Set(selectedImages);
        if (newSelected.has(imageId)) {
            newSelected.delete(imageId);
        } else {
            newSelected.add(imageId);
        }
        setSelectedImages(newSelected);
    };

    const selectAllPending = () => {
        const pendingIds = images
            .filter(img => img.status === 'pending-review' && !img.error)
            .map(img => img.id);
        setSelectedImages(new Set(pendingIds));
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'pending': return <Clock size={20} className="status-icon pending" />;
            case 'processing': return <Loader2 size={20} className="status-icon processing spin" />;
            case 'completed': return <CheckCircle size={20} className="status-icon completed" />;
            case 'paused': return <Pause size={20} className="status-icon paused" />;
            case 'cancelled': return <XCircle size={20} className="status-icon cancelled" />;
            case 'failed': return <XCircle size={20} className="status-icon failed" />;
            case 'continued': return <CheckCircle size={20} className="status-icon continued" />;
            case 'stuck-recovered': return <Clock size={20} className="status-icon recovered" />;
            default: return null;
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'pending': return 'En attente';
            case 'processing': return 'En cours';
            case 'completed': return 'Terminé';
            case 'paused': return 'En pause';
            case 'cancelled': return 'Annulé';
            case 'failed': return 'Échoué';
            case 'continued': return 'Continué';
            case 'stuck-recovered': return 'Récupéré';
            default: return status;
        }
    };

    const pendingReviewCount = images.filter(img => img.status === 'pending-review' && !img.error).length;
    const approvedCount = images.filter(img => img.status === 'approved').length;
    const uploadedCount = images.filter(img => img.status === 'uploaded').length;

    if (isLoading) {
        return (
            <div className="automation-detail-page">
                <div className="loading-state">
                    <Loader2 size={32} className="spin" />
                    <p>Chargement...</p>
                </div>
            </div>
        );
    }

    if (!job) {
        return (
            <div className="automation-detail-page">
                <div className="error-state">
                    <XCircle size={48} />
                    <h2>Automatisation introuvable</h2>
                    <Button variant="primary" onClick={() => navigate('/automations')}>
                        Retour aux automatisations
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="automation-detail-page">
            <div className="page-header">
                <button className="back-btn" onClick={() => navigate('/automations')}>
                    <ArrowLeft size={20} />
                    Retour
                </button>
            </div>

            {/* Job Info Card */}
            <div className="job-info-card glass">
                <div className="job-status-header">
                    {getStatusIcon(job.status)}
                    <span className={`status-label ${job.status}`}>
                        {getStatusLabel(job.status)}
                    </span>
                    {job.status === 'processing' && (
                        <Button
                            variant="ghost"
                            size="sm"
                            leftIcon={<XCircle size={16} />}
                            onClick={handleCancel}
                        >
                            Annuler
                        </Button>
                    )}
                </div>

                <h1 className="job-title">{job.promptTitle}</h1>

                <div className="job-meta">
                    <span className="meta-item">
                        <Store size={16} />
                        {job.shopName}
                    </span>
                    <span className="meta-item">
                        <ImageIcon size={16} />
                        {job.products.length} produits
                    </span>
                    <span className="mode-badge">{job.mode === 'auto-upload' ? 'Upload auto' : 'Vérification manuelle'}</span>
                </div>

                {/* Progress Section */}
                <div className="progress-section">
                    <div className="progress-bar-large">
                        <div
                            className="progress-fill"
                            style={{ width: `${((job.progress.completed + job.progress.failed) / job.progress.total) * 100}%` }}
                        />
                    </div>
                    <div className="progress-stats">
                        <div className="stat">
                            <span className="stat-value">{job.progress.completed}</span>
                            <span className="stat-label">Générées</span>
                        </div>
                        {job.progress.failed > 0 && (
                            <div className="stat error">
                                <span className="stat-value">{job.progress.failed}</span>
                                <span className="stat-label">Échouées</span>
                            </div>
                        )}
                        <div className="stat">
                            <span className="stat-value">{job.progress.total - job.progress.completed - job.progress.failed}</span>
                            <span className="stat-label">Restantes</span>
                        </div>
                        {job.status === 'processing' && job.estimatedTimeMs && (
                            <div className="stat highlight">
                                <span className="stat-value">{formatEstimatedTime(calculateRemainingTime(job))}</span>
                                <span className="stat-label">Temps restant</span>
                            </div>
                        )}
                    </div>

                    {/* Current product being processed */}
                    {job.status === 'processing' && job.progress.current && (
                        <div className="current-product">
                            <Loader2 size={16} className="spin" />
                            <span>En cours : {job.products.find(p => p.id === job.progress.current)?.title || 'Produit'}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Review Section (only for review mode) */}
            {job.mode === 'review' && images.length > 0 && (
                <div className="review-section glass">
                    <div className="review-header">
                        <h2>
                            <Eye size={20} />
                            Images à valider
                        </h2>
                        <div className="review-stats">
                            <span className="stat-badge pending">{pendingReviewCount} en attente</span>
                            <span className="stat-badge approved">{approvedCount} approuvées</span>
                            <span className="stat-badge uploaded">{uploadedCount} uploadées</span>
                        </div>
                    </div>

                    {pendingReviewCount > 0 && (
                        <div className="review-actions">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={selectAllPending}
                            >
                                Tout sélectionner ({pendingReviewCount})
                            </Button>
                            {selectedImages.size > 0 && (
                                <>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleClearSelection}
                                    >
                                        Désélectionner ({selectedImages.size})
                                    </Button>
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        leftIcon={<CheckCircle size={16} />}
                                        onClick={handleBulkApprove}
                                    >
                                        Approuver ({selectedImages.size})
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        leftIcon={<RotateCcw size={16} />}
                                        onClick={handleBulkReset}
                                    >
                                        Réinitialiser ({selectedImages.size})
                                    </Button>
                                </>
                            )}
                        </div>
                    )}

                    {approvedCount > 0 && (
                        <div className="upload-action">
                            <Button
                                variant="primary"
                                leftIcon={isUploading ? <Loader2 size={18} className="spin" /> : <Upload size={18} />}
                                onClick={handleUploadApproved}
                                disabled={isUploading}
                            >
                                {isUploading
                                    ? `Upload en cours... (${uploadProgress.uploaded}/${uploadProgress.total})`
                                    : `Uploader les images approuvées (${approvedCount})`
                                }
                            </Button>
                        </div>
                    )}

                    <div className="images-grid">
                        {images.map((image) => (
                            <div
                                key={image.id}
                                className={`image-card ${image.status} ${selectedImages.has(image.id) ? 'selected' : ''}`}
                            >
                                {image.error || image.status === 'failed' ? (
                                    <div className="image-wrapper">
                                        <div className="image-error">
                                            <XCircle size={32} />
                                            <p>Erreur</p>
                                            <span className="error-text">{image.error || 'Génération échouée'}</span>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="image-wrapper">
                                            <img
                                                src={image.imageUrl}
                                                alt={image.productTitle}
                                            />
                                            <div className="image-overlay">
                                                <span className="product-name">{image.productTitle}</span>
                                                <div className={`status-badge ${image.status}`}>
                                                    {image.status === 'pending-review' && 'En attente'}
                                                    {image.status === 'approved' && 'Approuvée'}
                                                    {image.status === 'uploaded' && 'Uploadée'}
                                                    {image.status === 'rejected' && 'Rejetée'}
                                                </div>
                                            </div>
                                            {/* Upload indicator overlay */}
                                            {(image.status === 'approved' || image.status === 'uploaded') && (
                                                <div className={`upload-indicator ${image.uploadedToShopify ? 'uploaded' : 'pending'}`}>
                                                    {image.uploadedToShopify ? (
                                                        <CheckCircle size={16} />
                                                    ) : (
                                                        <Upload size={16} />
                                                    )}
                                                </div>
                                            )}
                                            {/* Top-right action buttons */}
                                            <div className="image-top-actions">
                                                <button
                                                    className="top-action-btn zoom"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setZoomedImage({ url: image.imageUrl, title: image.productTitle });
                                                    }}
                                                    title="Zoom (ouvrir en grand)"
                                                >
                                                    <ZoomIn size={16} />
                                                </button>
                                                {image.status === 'pending-review' && (
                                                    <button
                                                        className={`top-action-btn select ${selectedImages.has(image.id) ? 'selected' : ''}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleImageSelection(image.id);
                                                        }}
                                                        title={selectedImages.has(image.id) ? 'Désélectionner' : 'Sélectionner'}
                                                    >
                                                        <Check size={16} />
                                                    </button>
                                                )}
                                            </div>
                                            {image.status === 'pending-review' && (
                                                <div className="image-actions">
                                                    <button
                                                        className="action-btn approve"
                                                        onClick={() => handleApprove(image.id)}
                                                        title="Approuver"
                                                    >
                                                        <CheckCircle size={20} />
                                                    </button>
                                                    <button
                                                        className="action-btn reject"
                                                        onClick={() => handleReject(image.id)}
                                                        title="Rejeter"
                                                    >
                                                        <XCircle size={20} />
                                                    </button>
                                                </div>
                                            )}
                                            {(image.status === 'approved' || image.status === 'rejected') && (
                                                <div className="image-actions">
                                                    <button
                                                        className="action-btn reset"
                                                        onClick={() => handleReset(image.id)}
                                                        title="Réinitialiser (remettre en attente)"
                                                    >
                                                        <RotateCcw size={20} />
                                                    </button>
                                                </div>
                                            )}
                                            {selectedImages.has(image.id) && (
                                                <div className="selection-indicator">
                                                    <CheckCircle size={24} />
                                                </div>
                                            )}
                                            {/* Prompt toggle button */}
                                            {image.usedPrompt && (
                                                <button
                                                    className="prompt-toggle-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        togglePromptExpand(image.id);
                                                    }}
                                                    title={expandedPrompts.has(image.id) ? 'Masquer le prompt' : 'Voir le prompt envoyé'}
                                                >
                                                    {expandedPrompts.has(image.id) ? <EyeOff size={16} /> : <Eye size={16} />}
                                                </button>
                                            )}
                                        </div>
                                    </>
                                )}
                                {/* Prompt display below card */}
                                {expandedPrompts.has(image.id) && image.usedPrompt && (
                                    <div className="prompt-info-section">
                                        <div className="prompt-info-header">
                                            <strong>Prompt envoyé à l'IA :</strong>
                                        </div>
                                        <pre className="prompt-info-text">{image.usedPrompt}</pre>
                                    </div>
                                )}
                                {/* SEO Fields - Always visible for pending-review and approved images */}
                                {(image.status === 'pending-review' || image.status === 'approved') && seoEdits[image.id] && (
                                    <div className="seo-fields-section">
                                        <div className="seo-field">
                                            <label>
                                                <FileText size={14} />
                                                Nom du fichier
                                            </label>
                                            <div className="seo-input-wrapper">
                                                <input
                                                    type="text"
                                                    value={seoEdits[image.id]?.filename || ''}
                                                    onChange={(e) => updateSeoEdit(image.id, 'filename', e.target.value)}
                                                    placeholder="nom-du-fichier"
                                                    disabled={image.status === 'approved'}
                                                />
                                                <span className="file-extension">.webp</span>
                                            </div>
                                        </div>
                                        <div className="seo-field">
                                            <label>
                                                <Type size={14} />
                                                Texte alternatif
                                            </label>
                                            <input
                                                type="text"
                                                value={seoEdits[image.id]?.altText || ''}
                                                onChange={(e) => updateSeoEdit(image.id, 'altText', e.target.value)}
                                                placeholder="Description de l'image"
                                                disabled={image.status === 'approved'}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Auto-upload mode: Show generated images */}
            {job.mode === 'auto-upload' && images.length > 0 && (
                <div className="generated-section glass">
                    <div className="section-header">
                        <h2>
                            <ImageIcon size={20} />
                            Images générées
                        </h2>
                        <span className="count">{uploadedCount} uploadées sur Shopify</span>
                    </div>

                    <div className="images-grid auto-mode">
                        {images.map((image) => (
                            <div key={image.id} className={`image-card ${image.status}`}>
                                {image.error || image.status === 'failed' ? (
                                    <div className="image-wrapper">
                                        <div className="image-error">
                                            <XCircle size={32} />
                                            <p>Erreur</p>
                                            <span className="error-text">{image.error || 'Génération échouée'}</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="image-wrapper">
                                        <img
                                            src={image.imageUrl}
                                            alt={image.productTitle}
                                        />
                                        <div className="image-overlay">
                                            <span className="product-name">{image.productTitle}</span>
                                            {image.uploadedToShopify && (
                                                <div className="status-badge uploaded">
                                                    <CheckCircle size={12} />
                                                    Uploadée
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Zoom Modal */}
            {zoomedImage && (
                <div className="zoom-modal-overlay" onClick={() => setZoomedImage(null)}>
                    <div className="zoom-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="zoom-modal-close" onClick={() => setZoomedImage(null)}>
                            <X size={24} />
                        </button>
                        <img src={zoomedImage.url} alt={zoomedImage.title} />
                        <div className="zoom-modal-title">{zoomedImage.title}</div>
                    </div>
                </div>
            )}
        </div>
    );
}
